import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../hooks/useAuth';
import pettyCashService from '../../../services/pettyCashService';

const PettyCashTransactionsTable = ({ onClose, refreshTrigger = 0 }) => {
  const { userProfile } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all', // 'all', 'expense', 'replenishment\'dateFrom: '',dateTo: '',status: 'all' // 'all', 'posted', 'draft'
  });
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ 
    key: 'entryDate', 
    direction: 'desc' 
  });

  const organizationId = userProfile?.organization_id;

  // Load transactions
  const loadTransactions = async () => {
    if (!organizationId) {
      setError('No organization found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const transactionsData = await pettyCashService?.getPettyCashTransactions(organizationId, {
        limit: 100 // Load more transactions for the table view
      });
      
      setTransactions(transactionsData || []);
      setFilteredTransactions(transactionsData || []);
    } catch (err) {
      console.error('Error loading petty cash transactions:', err);
      setError(err?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  // Load transactions on mount and when refreshTrigger changes
  useEffect(() => {
    loadTransactions();
  }, [organizationId, refreshTrigger]);

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...transactions];

    // Search filter
    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter(t => 
        t?.description?.toLowerCase()?.includes(searchTerm) ||
        t?.entryNumber?.toLowerCase()?.includes(searchTerm) ||
        t?.reference?.toLowerCase()?.includes(searchTerm)
      );
    }

    // Type filter
    if (filters?.type !== 'all') {
      filtered = filtered?.filter(t => t?.transactionType === filters?.type);
    }

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(t => t?.status === filters?.status);
    }

    // Date filters
    if (filters?.dateFrom) {
      filtered = filtered?.filter(t => t?.entryDate >= filters?.dateFrom);
    }
    if (filters?.dateTo) {
      filtered = filtered?.filter(t => t?.entryDate <= filters?.dateTo);
    }

    // Sorting
    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = a?.[sortConfig?.key];
        let bValue = b?.[sortConfig?.key];

        // Handle different data types
        if (sortConfig?.key === 'amount') {
          aValue = Math.abs(parseFloat(aValue));
          bValue = Math.abs(parseFloat(bValue));
        } else if (sortConfig?.key === 'entryDate') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (typeof aValue === 'string') {
          aValue = aValue?.toLowerCase();
          bValue = bValue?.toLowerCase();
        }

        if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredTransactions(filtered);
  }, [transactions, filters, sortConfig]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectTransaction = (transactionId) => {
    setSelectedTransactions(prev => {
      if (prev?.includes(transactionId)) {
        return prev?.filter(id => id !== transactionId);
      } else {
        return [...prev, transactionId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedTransactions?.length === filteredTransactions?.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(filteredTransactions?.map(t => t?.id));
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      type: 'all',
      dateFrom: '',
      dateTo: '',
      status: 'all'
    });
  };

  const exportTransactions = () => {
    const dataToExport = filteredTransactions?.map(transaction => ({
      'Entry Date': formatDate(transaction?.entryDate),
      'Entry Number': transaction?.entryNumber,
      'Description': transaction?.description,
      'Type': transaction?.transactionType?.charAt(0)?.toUpperCase() + transaction?.transactionType?.slice(1),
      'Amount': Math.abs(transaction?.amount),
      'Reference': transaction?.reference || '',
      'Status': transaction?.status?.charAt(0)?.toUpperCase() + transaction?.status?.slice(1)
    }));

    const csvContent = [
      Object.keys(dataToExport?.[0] || {})?.join(','),
      ...dataToExport?.map(row => Object.values(row)?.map(val => `"${val}"`)?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link?.setAttribute('href', url);
    link?.setAttribute('download', `petty-cash-transactions-${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    link?.click();
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(Math.abs(amount) || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) return '↕️';
    return sortConfig?.direction === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-card rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-foreground">Petty Cash Transactions</h2>
              <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
            </div>
          </div>
          <div className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-10 bg-muted rounded w-full"></div>
              <div className="h-64 bg-muted rounded w-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-foreground">Petty Cash Transactions</h2>
              <p className="text-muted-foreground text-sm mt-1">
                {filteredTransactions?.length} of {transactions?.length} transactions
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportTransactions}
                iconName="Download"
                iconPosition="left"
                disabled={filteredTransactions?.length === 0}
              >
                Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadTransactions}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Refresh
              </Button>
              <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-border bg-muted/20">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <Input
                placeholder="Search transactions..."
                value={filters?.search}
                onChange={(e) => handleFilterChange('search', e?.target?.value)}
                iconName="Search"
              />
            </div>
            <div>
              <Select
                value={filters?.type}
                onChange={(value) => handleFilterChange('type', value)}
                options={[
                  { value: 'all', label: 'All Types' },
                  { value: 'expense', label: 'Expenses' },
                  { value: 'replenishment', label: 'Replenishments' }
                ]}
              />
            </div>
            <div>
              <Select
                value={filters?.status}
                onChange={(value) => handleFilterChange('status', value)}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'posted', label: 'Posted' },
                  { value: 'draft', label: 'Draft' }
                ]}
              />
            </div>
            <div>
              <Input
                type="date"
                placeholder="From date"
                value={filters?.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
              />
            </div>
            <div className="flex space-x-2">
              <Input
                type="date"
                placeholder="To date"
                value={filters?.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                iconName="X"
              >
                Clear
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {error ? (
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-error text-lg">⚠️</span>
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Error Loading Transactions</h3>
              <p className="text-muted-foreground text-sm mb-4">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={loadTransactions}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Try Again
              </Button>
            </div>
          ) : filteredTransactions?.length === 0 ? (
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-muted-foreground text-lg">📝</span>
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No Transactions Found</h3>
              <p className="text-muted-foreground text-sm">
                {transactions?.length === 0 
                  ? 'No petty cash transactions have been recorded yet.' :'No transactions match your current filters.'
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/30 border-b border-border">
                  <tr>
                    <th className="text-left p-3 w-10">
                      <input
                        type="checkbox"
                        checked={selectedTransactions?.length === filteredTransactions?.length && filteredTransactions?.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th 
                      className="text-left p-3 font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('entryDate')}
                    >
                      Date {getSortIcon('entryDate')}
                    </th>
                    <th 
                      className="text-left p-3 font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('entryNumber')}
                    >
                      Entry # {getSortIcon('entryNumber')}
                    </th>
                    <th 
                      className="text-left p-3 font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('description')}
                    >
                      Description {getSortIcon('description')}
                    </th>
                    <th 
                      className="text-left p-3 font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('transactionType')}
                    >
                      Type {getSortIcon('transactionType')}
                    </th>
                    <th 
                      className="text-right p-3 font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('amount')}
                    >
                      Amount {getSortIcon('amount')}
                    </th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Reference</th>
                    <th 
                      className="text-left p-3 font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('status')}
                    >
                      Status {getSortIcon('status')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions?.map((transaction) => (
                    <tr 
                      key={transaction?.id} 
                      className="border-b border-border hover:bg-muted/10"
                    >
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedTransactions?.includes(transaction?.id)}
                          onChange={() => handleSelectTransaction(transaction?.id)}
                        />
                      </td>
                      <td className="p-3 text-sm text-foreground">
                        {formatDate(transaction?.entryDate)}
                      </td>
                      <td className="p-3 text-sm font-medium text-foreground">
                        {transaction?.entryNumber}
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {transaction?.description}
                          </p>
                          {transaction?.notes && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {transaction?.notes}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          transaction?.transactionType === 'expense' ?'bg-red-100 text-red-700' :'bg-green-100 text-green-700'
                        }`}>
                          {transaction?.transactionType === 'expense' ? '📉' : '📈'} 
                          {transaction?.transactionType?.charAt(0)?.toUpperCase() + transaction?.transactionType?.slice(1)}
                        </span>
                      </td>
                      <td className={`p-3 text-right text-sm font-semibold ${
                        transaction?.transactionType === 'expense' ? 'text-error' : 'text-success'
                      }`}>
                        {transaction?.transactionType === 'expense' ? '-' : '+'}
                        {formatCurrency(transaction?.amount)}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {transaction?.reference || '-'}
                      </td>
                      <td className="p-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          transaction?.status === 'posted' ?'bg-green-100 text-green-700' :'bg-yellow-100 text-yellow-700'
                        }`}>
                          {transaction?.status?.charAt(0)?.toUpperCase() + transaction?.status?.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer with selection info */}
        {selectedTransactions?.length > 0 && (
          <div className="p-4 border-t border-border bg-muted/20">
            <div className="flex items-center justify-between">
              <p className="text-sm text-foreground">
                {selectedTransactions?.length} transaction{selectedTransactions?.length !== 1 ? 's' : ''} selected
              </p>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedTransactions([])}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PettyCashTransactionsTable;